import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Controller from '@ember/controller';
import { isNone } from '@ember/utils';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { Platform, PlatformProvider } from 'cio/models/action';
import { importSync } from '@embroider/macros';

const DEFAULT_IOS_PROVIDER = 'apns';

export default class IosController extends Controller {
  @service push;
  @service analytics;

  @tracked isConfigurationEmpty = false;
  @tracked isEditing = false;
  @tracked provider = DEFAULT_IOS_PROVIDER;
  @tracked _initialProvider = null;
  @tracked isConfigDirty = false;
  @tracked androidEnabled = false;

  get component() {
    let module = importSync(`../../../../components/push-provider-config/${this.provider ?? DEFAULT_IOS_PROVIDER}`);

    return module.default;
  }

  get isDirty() {
    return this.isConfigDirty;
  }

  get showEmptyState() {
    return this.isConfigurationEmpty && !this.isEditing;
  }

  get platform() {
    return this.get('push.platforms').findBy('key', 'ios');
  }

  get showDisable() {
    let configuredProvider = this._initialProvider;
    let currentProvider = this.provider;
    return !this.isConfigurationEmpty && configuredProvider === currentProvider && currentProvider === 'apns';
  }

  refreshProvider() {
    let { platform } = this;

    if (!isNone(platform)) {
      this.setProperties({
        _initialProvider: platform.provider,
        provider: platform.provider ?? DEFAULT_IOS_PROVIDER,
        isConfigurationEmpty: false,
      });
    } else {
      this.setProperties({
        isConfigurationEmpty: true,
        provider: DEFAULT_IOS_PROVIDER,
      });
    }
  }

  save = task(async (configSave) => {
    this.trackAndroidConfigured();
    await configSave();
    this.refreshProvider();
  });

  trackAndroidConfigured() {
    let fcm = this.push.platforms.find((p) => p.provider === PlatformProvider.FCM);
    if (fcm !== undefined) {
      this.androidEnabled = this.containsAndroid(fcm.platform);
    } else {
      this.androidEnabled = false;
    }
  }

  containsAndroid(platform) {
    if (typeof platform === 'string') {
      return platform.toLowerCase() === 'android';
    } else if (Array.isArray(platform)) {
      return platform.indexOf('android') !== -1;
    }

    return false;
  }

  @action
  reset(configReset) {
    if (configReset) {
      configReset();
    }

    this.provider = this._initialProvider ?? DEFAULT_IOS_PROVIDER;
  }

  @action
  startConfiguring() {
    this.isEditing = true;
  }

  @action
  afterSave() {
    this.analytics.track('push', !this.isConfigurationEmpty ? 'authorization_updated' : 'authorization_added', {
      platform: Platform.iOS,
      provider: this.provider,
    });
    if (this.provider === PlatformProvider.FCM && this.androidEnabled === false) {
      this.analytics.track('push', 'authorization_added', {
        platform: Platform.Android,
        provider: PlatformProvider.FCM,
      });
    }
    this.isConfigurationEmpty = false;
  }

  @action
  async disableCredential() {
    await this.push.disableCredential.perform('apns');
    this.isConfigurationEmpty = true;
    this.analytics.track('push', 'push_disabled', {
      platform: Platform.iOS,
      provider: this.provider,
    });
  }

  @action
  onIsDirtyChange(isDirty) {
    this.isConfigDirty = isDirty;
  }
}
